'use client';

import type { MouseEvent, ReactNode } from 'react';
import { PrismicNextLink } from '@prismicio/next';
import { usePrefersReducedMotion } from '@/hooks';
import { normalizeAnchor, scrollToAnchor } from '@/utils/anchor-link-utils';

type Props = {
  /** Raw editor value — normalized here, with or without a leading `#`. */
  anchor: string;
  className?: string;
  children: ReactNode;
};

/**
 * Client leaf for CTAs that scroll to a section on the same page.
 *
 * Kept separate from the CTA components themselves so their default,
 * link-field path stays a server component: pages that use no anchors ship no
 * extra client JS for this feature.
 *
 * Owns the normalization because it is the one place that needs the clean value
 * twice — once to build the `href` at render time, once to resolve the target at
 * click time. Callers only have to answer "is there an anchor?" (`hasAnchor`).
 *
 * The `href` keeps this a real link (keyboard, focus, middle-click) and resolves
 * natively when the anchor is an element id. The handler only takes over when
 * the target had to be found by `data-slice-type`, which a plain hash can't do.
 */
export function AnchorCtaLink({ anchor, className, children }: Props) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const normalized = normalizeAnchor(anchor);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (scrollToAnchor(normalized, { smooth: !prefersReducedMotion })) {
      event.preventDefault();
    }
  };

  return (
    <PrismicNextLink
      href={`#${normalized ?? ''}`}
      onClick={handleClick}
      className={className}
    >
      {children}
    </PrismicNextLink>
  );
}
